import axios from 'axios';
import i18n from './i18n';
import authProvider from '../auth/auth0-provider';
import moment from 'moment-timezone';

const BEARER_PREFIX = 'Bearer ';
const UNAUTHORIZED = 401;
const RETRY_LIMIT_401 = 1; // how many times to retry a request with a new JWT if the API returns a 401.
const JWT_SECTION_DELIMITER = '.';
const JWT_BODY_INDEX = 1; // JWT format is `header.body.signature`
let _sessionTimeoutModalRef;

// taken from https://www.w3schools.com/js/js_cookies.asp
function getCookie(cname) {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

function addLanguageHeader(headers) {
  let language = 'en';

  try {
    language = i18n.getLanguage();
  } catch {
    // if i18n hasn't been initialized fully, there could be an error here.
    // intentionally ignored, and just send default language.
  }

  headers['X-Language'] = language;
}

function isTokenExpired(authorizationHeader) {
  if (!authorizationHeader) {
    return true;
  }

  try {
    const expiration = JSON.parse(atob(authorizationHeader.split(JWT_SECTION_DELIMITER)[JWT_BODY_INDEX])).exp;
    return expiration === undefined || expiration <= moment().unix();
  } catch {
    // If there is an error processing the token, call it "expired" so that a new valid JWT is obtained.
    return true;
  }
}

function handleUnauthorized(error) {
  if (error.config?.headers?.['X-Attempt'] === undefined) {
    return Promise.reject(error);
  }

  const attempt = parseInt(error.config.headers['X-Attempt']);

  if (attempt <= RETRY_LIMIT_401 && isTokenExpired(error.config?.headers?.Authorization)) {
    console.warn(`Attempt ${attempt} received 401. Retrying request.`);
    return axios.request(error.config);
  }

  console.warn(`Attempt ${attempt} received 401. Redirecting to signin.`);
  authProvider.getContext().forceInteractiveLogin();

  return Promise.reject(error);
}

async function addAuthorizationHeader(headers) {
  const authContext = authProvider.getContext();
  if (headers.Authorization || !authContext.isAuthenticated) {
    return;
  }

  const authToken = await authContext.getToken();
  if (authToken) {
    headers.Authorization = `${BEARER_PREFIX}${authToken}`;
  }
}

axios.defaults.headers.common = {
  'X-Requested-With': 'XMLHttpRequest',
};

axios.interceptors.request.use(
  async config => {
    config.withCredentials = true;
    config.headers.Accept = 'application/json';
    config.headers['X-CSRF-Token'] = getCookie('XSRF-TOKEN');
    config.headers['X-Attempt'] = config.headers['X-Attempt'] || 0;
    config.headers['X-Attempt'] = parseInt(config.headers['X-Attempt']) + 1;
    addLanguageHeader(config.headers);
    await addAuthorizationHeader(config.headers);
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  response => {
    if (!response?.config?.headers?.['X-No-Session-Refresh']) {
      _sessionTimeoutModalRef?.resetSessionTimers();
    }
    return response;
  },
  error => {
    if (error?.response?.status === UNAUTHORIZED) {
      return handleUnauthorized(error);
    }
    return Promise.reject(error);
  },
);

export default {
  init(sessionTimeoutModalRef) {
    _sessionTimeoutModalRef = sessionTimeoutModalRef;
  },
};
